import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ErrorAlert } from '../components/ErrorAlert';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  useChangePassword,
  useConfirmTotp,
  useLogin,
  useSetupTotp,
  useVerifyTotp,
} from '../api/queries';
import { setSessionToken } from '../auth/useAccessToken';
import { AuthShell } from '../components/auth/AuthShell';
import { OtpInput } from '../components/auth/OtpInput';
import { PasswordField } from '../components/auth/PasswordField';
import { describePasswordPolicy, validatePassword, type PasswordPolicy } from '../auth/passwordPolicy';

type Mode =
  | { kind: 'password' }
  | { kind: 'change-password'; tempToken: string; oldPassword: string; policy: PasswordPolicy }
  | { kind: 'setup-totp'; tempToken: string; qrCodeDataUri: string; manualEntryKey: string }
  | { kind: 'backup-codes-shown'; codes: string[] }
  | { kind: 'verify-totp'; tempToken: string };

// Username remembered across browser sessions in localStorage when the
// user ticks the "Remember me" box. Password and tokens are never
// persisted here — only the username, so the next visit can pre-fill
// the field without leaking credentials.
const REMEMBERED_USERNAME_KEY = 'arms.login.rememberedUsername';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // ?reason=expired arrives from two paths: the eager exp-claim timer
  // and the 401 handler in apiFetch. Either way we want to tell the
  // user *why* they're back at /login so it doesn't feel like a glitch.
  const sessionExpired = searchParams.get('reason') === 'expired';
  const sessionIdle    = searchParams.get('reason') === 'idle';
  // RequireAuth stashes the page the user was actually headed to (path +
  // query string — e.g. the Machine Comm Analyser's `?consoleCapture=…`
  // handoff, or an expired `?consoleAuthHandoff=…` one) on
  // `location.state.from` before bouncing here. Honour it on a successful
  // sign-in instead of always landing on /home — otherwise a manual login
  // after an expired auth handoff would strand the user on the dashboard
  // having lost the capture-handoff context entirely.
  const from = (location.state as { from?: string } | null)?.from || '/home';
  const [mode, setMode] = useState<Mode>({ kind: 'password' });

  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  // Pre-fill from localStorage on first mount. If a value is present
  // we also tick the box so the user can see why their name is there.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const remembered = window.localStorage.getItem(REMEMBERED_USERNAME_KEY);
    if (remembered) {
      setUsernameOrEmail(remembered);
      setRememberMe(true);
    }
  }, []);

  const login          = useLogin();
  const setup          = useSetupTotp();
  const confirm        = useConfirmTotp();
  const verify         = useVerifyTotp();
  const changePassword = useChangePassword();

  const [newPassword,    setNewPassword]    = useState('');
  const [confirmNewPwd,  setConfirmNewPwd]  = useState('');

  const error = (login.error || setup.error || confirm.error || verify.error || changePassword.error) as { message?: string } | null;
  const busy  = login.isPending || setup.isPending || confirm.isPending || verify.isPending || changePassword.isPending;

  const onPasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    // Persist (or clear) the username before the network call so the
    // user sees the box reflect their preference even if the login
    // fails. Never persist the password.
    if (typeof window !== 'undefined') {
      if (rememberMe) {
        window.localStorage.setItem(REMEMBERED_USERNAME_KEY, usernameOrEmail.trim());
      } else {
        window.localStorage.removeItem(REMEMBERED_USERNAME_KEY);
      }
    }
    const res = await login.mutateAsync({ usernameOrEmail, password });
    // Force a password change BEFORE 2FA when the user is on a temp /
    // expired password. Stops "I logged in once with the temp pw and
    // it just keeps working" — the next login still wants the old
    // temp password until they explicitly set a new one.
    if (res.requiresPasswordChange) {
      setMode({
        kind: 'change-password',
        tempToken: res.tempToken,
        oldPassword: password,
        policy: res.passwordPolicy,
      });
      setNewPassword('');
      setConfirmNewPwd('');
      return;
    }
    if (res.requiresTotpSetup) {
      const qr = await setup.mutateAsync(res.tempToken);
      setMode({
        kind: 'setup-totp',
        tempToken: res.tempToken,
        qrCodeDataUri: qr.qrCodeDataUri,
        manualEntryKey: qr.manualEntryKey,
      });
      setCode('');
    } else {
      setMode({ kind: 'verify-totp', tempToken: res.tempToken });
      setCode('');
    }
  };

  const onChangePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (mode.kind !== 'change-password') return;
    if (!validatePassword(newPassword, mode.policy) || newPassword !== confirmNewPwd) return;

    await changePassword.mutateAsync({
      tempToken:       mode.tempToken,
      currentPassword: mode.oldPassword,
      newPassword,
    });

    // Re-issue login with the new password so we get a fresh temp
    // token whose `requires_password_change` claim is false. Then
    // continue into the TOTP step exactly like a normal login would.
    setPassword(newPassword);
    const res = await login.mutateAsync({ usernameOrEmail, password: newPassword });
    setNewPassword('');
    setConfirmNewPwd('');
    if (res.requiresTotpSetup) {
      const qr = await setup.mutateAsync(res.tempToken);
      setMode({
        kind: 'setup-totp',
        tempToken: res.tempToken,
        qrCodeDataUri: qr.qrCodeDataUri,
        manualEntryKey: qr.manualEntryKey,
      });
      setCode('');
    } else {
      setMode({ kind: 'verify-totp', tempToken: res.tempToken });
      setCode('');
    }
  };

  const onConfirmSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (mode.kind !== 'setup-totp') return;
    const res = await confirm.mutateAsync({ tempToken: mode.tempToken, code });
    setPassword('');
    setCode('');
    setMode({ kind: 'backup-codes-shown', codes: res.backup_codes ?? [] });
  };

  const onConfirmAuto = async (autoCode: string) => {
    if (mode.kind !== 'setup-totp') return;
    const res = await confirm.mutateAsync({ tempToken: mode.tempToken, code: autoCode });
    setPassword('');
    setCode('');
    setMode({ kind: 'backup-codes-shown', codes: res.backup_codes ?? [] });
  };

  const onVerifySubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (mode.kind !== 'verify-totp') return;
    const result = await verify.mutateAsync({ tempToken: mode.tempToken, code });
    setSessionToken(result.token);
    navigate(from);
  };

  const onVerifyAuto = async (autoCode: string) => {
    if (mode.kind !== 'verify-totp') return;
    const result = await verify.mutateAsync({ tempToken: mode.tempToken, code: autoCode });
    setSessionToken(result.token);
    navigate(from);
  };

  const title    = mode.kind === 'password'            ? 'Sign in'
                 : mode.kind === 'change-password'     ? 'Set a new password'
                 : mode.kind === 'setup-totp'          ? 'Set up two-factor'
                 : mode.kind === 'backup-codes-shown'  ? 'Save your backup codes'
                 : 'Two-factor code';
  const subtitle = mode.kind === 'password'            ? 'Enter your username or email and password.'
                 : mode.kind === 'change-password'     ? 'Your account is on a temporary password — choose a new one before continuing.'
                 : mode.kind === 'setup-totp'          ? 'Scan the QR with any authenticator — iCloud Passwords (iPhone Settings → Passwords), Microsoft / Google Authenticator, Authy, 1Password — then enter the 6-digit code below.'
                 : mode.kind === 'backup-codes-shown'  ? 'Each code works once if you ever lose your authenticator. Save them somewhere safe — they will not be shown again.'
                 : 'Enter the 6-digit code from your authenticator app.';

  return (
    <AuthShell
      title={title}
      subtitle={subtitle}
      topSlot={
        error?.message ? (
          <ErrorAlert>{error.message}</ErrorAlert>
        ) : sessionExpired && mode.kind === 'password' ? (
          <Alert severity="info">
            Your session has expired. Please sign in again.
          </Alert>
        ) : sessionIdle ? (
          <Alert severity="info">
            You were signed out after a period of inactivity. Please sign in again.
          </Alert>
        ) : undefined
      }
    >
      {mode.kind === 'password' && (
        <form onSubmit={onPasswordSubmit}>
          <Stack spacing={2}>
            <TextField
              label="Username or email"
              autoFocus
              autoComplete="username"
              value={usernameOrEmail}
              onChange={(e) => setUsernameOrEmail(e.target.value)}
              disabled={busy}
              required
              fullWidth
            />
            <PasswordField
              label="Password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
              required
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={busy}
                  size="small"
                />
              }
              label="Remember my username"
              sx={{ mt: -0.5 }}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={busy || !usernameOrEmail.trim() || !password}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ py: 1.25, fontWeight: 600, mt: 1 }}
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
            <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 1 }}>
              First time here? <Link to="/setup">Set up the platform</Link>.
            </Typography>
          </Stack>
        </form>
      )}

      {mode.kind === 'change-password' && (
        <form onSubmit={onChangePasswordSubmit}>
          <Stack spacing={2}>
            <PasswordField
              label="New password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={busy}
              required
              fullWidth
              autoFocus
              helperText={describePasswordPolicy(mode.policy)}
            />
            <PasswordField
              label="Confirm new password"
              autoComplete="new-password"
              value={confirmNewPwd}
              onChange={(e) => setConfirmNewPwd(e.target.value)}
              disabled={busy}
              required
              fullWidth
              error={confirmNewPwd.length > 0 && newPassword !== confirmNewPwd}
              helperText={confirmNewPwd.length > 0 && newPassword !== confirmNewPwd
                ? 'Passwords do not match.' : ' '}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={busy || !validatePassword(newPassword, mode.policy) || newPassword !== confirmNewPwd}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ py: 1.25, fontWeight: 600, mt: 1 }}
            >
              {busy ? 'Updating…' : 'Set new password and continue'}
            </Button>
          </Stack>
        </form>
      )}

      {mode.kind === 'setup-totp' && (
        <form onSubmit={onConfirmSubmit}>
          <Stack spacing={2.5} alignItems="center">
            <Box
              component="img"
              src={mode.qrCodeDataUri}
              alt="TOTP QR code"
              sx={{
                width: 224,
                height: 224,
                p: 1,
                bgcolor: '#fff',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1.5,
                boxShadow: 1,
              }}
            />
            <Box
              sx={{
                bgcolor: 'action.hover',
                borderRadius: 1,
                p: 1.5,
                width: '100%',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                iPhone tip
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                Open the iPhone Camera and point it at the QR — iOS pops "Add Verification Code"
                straight into Settings → Passwords, so the code auto-fills next time you sign in
                here or on the ARMS Technician app.
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Manual entry key (paste into any authenticator)
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 0.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    bgcolor: 'action.hover',
                    px: 1.5, py: 0.75,
                    borderRadius: 1,
                    letterSpacing: 1,
                  }}
                >
                  {mode.manualEntryKey}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    navigator.clipboard.writeText(mode.manualEntryKey);
                  }}
                >
                  Copy
                </Button>
              </Stack>
            </Box>
            <OtpInput
              value={code}
              onChange={setCode}
              onComplete={onConfirmAuto}
              disabled={busy}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={busy || code.length !== 6}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ py: 1.25, fontWeight: 600 }}
            >
              {busy ? 'Confirming…' : 'Confirm and continue'}
            </Button>
            <Typography variant="caption" color="text.secondary" align="center">
              After confirming you'll sign in again — the fresh login carries
              the verified two-factor claim.
            </Typography>
          </Stack>
        </form>
      )}

      {mode.kind === 'backup-codes-shown' && (
        <Stack spacing={2}>
          <Box sx={{
            p: 2, borderRadius: 1, bgcolor: 'background.default',
            border: 1, borderColor: 'divider',
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1,
          }}>
            {mode.codes.map((c) => (
              <Typography key={c} variant="body2" sx={{
                fontFamily: 'monospace', fontSize: 14, textAlign: 'center', py: 0.5,
              }}>{c}</Typography>
            ))}
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" fullWidth onClick={() => {
              navigator.clipboard.writeText(mode.codes.join('\n'));
            }}>Copy all</Button>
            <Button variant="outlined" fullWidth onClick={() => {
              const blob = new Blob([mode.codes.join('\n')], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = 'arms-backup-codes.txt'; a.click();
              setTimeout(() => URL.revokeObjectURL(url), 5_000);
            }}>Download .txt</Button>
          </Stack>
          <Button variant="contained" fullWidth onClick={() => setMode({ kind: 'password' })}
            sx={{ py: 1.25, fontWeight: 600 }}>
            I&apos;ve saved them — sign in
          </Button>
        </Stack>
      )}

      {mode.kind === 'verify-totp' && (
        <form onSubmit={onVerifySubmit}>
          <Stack spacing={2}>
            <OtpInput
              value={code}
              onChange={setCode}
              onComplete={onVerifyAuto}
              disabled={busy}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={busy || code.length !== 6}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ py: 1.25, fontWeight: 600 }}
            >
              {busy ? 'Verifying…' : 'Verify and sign in'}
            </Button>
            <Button
              onClick={() => { setMode({ kind: 'password' }); setCode(''); }}
              disabled={busy}
            >
              Back
            </Button>
          </Stack>
        </form>
      )}
    </AuthShell>
  );
}
